import { ReactNode } from 'react';

import { QuestionnaireProgressTabProps } from '@backrs_ui/common-ui/src/atoms/QuestionnaireProgressTab';
import {
  Pagination,
  PaginationProps,
} from '@backrs_ui/common-ui/src/molecules/Pagination/Pagination';

import { Box, SxProps, Theme } from '@mui/material';

import { QuestionnaireProgressTabs } from './QuestionnaireProgressTabs';
import { SIDEBAR_COLLAPSED_WIDTH } from '../BackrsSidebar/BackrsSidebar';

export type QuestionnaireProps = Readonly<{
  tabs: QuestionnaireProgressTabProps[];
  activeTabId?: string;
  activeSubcategoryId?: string;
  onSubcategoryClick?: (tabId: string, subcategoryId: string) => void;
  paginationProps: PaginationProps;
  children: ReactNode;
  showSubcategories?: boolean;
}>;

export const Questionnaire = ({
  tabs,
  activeTabId,
  activeSubcategoryId,
  onSubcategoryClick,
  paginationProps,
  children,
  showSubcategories = true,
}: QuestionnaireProps) => {
  return (
    <Box sx={containerSx}>
      <Box sx={tabsContainerSx}>
        <QuestionnaireProgressTabs
          tabs={tabs}
          activeTabId={activeTabId}
          activeSubcategoryId={activeSubcategoryId}
          onSubcategoryClick={onSubcategoryClick}
          showSubcategories={showSubcategories}
        />
      </Box>

      <Box sx={contentContainerSx}>{children}</Box>

      <Box sx={paginationContainerSx}>
        <Pagination {...paginationProps} />
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
};

const tabsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  padding: backrs.spacing['spacing-2xl'],
  position: 'sticky',
  top: 0,
  zIndex: 1,
  maxWidth: '1280px',
  width: '100%',
  margin: '0 auto',
  backgroundColor: backrs.colors['Beige/25'],
  // target chromebook sizes screens
  '@media screen and (max-width: 1366px) and (max-height: 768px)': {
    marginLeft: `${SIDEBAR_COLLAPSED_WIDTH}px`,
    maxWidth: `calc(100% - ${SIDEBAR_COLLAPSED_WIDTH}px)`,
  },
  // target iPad Mini
  '@media screen and (max-width: 1024px) and (max-height: 768px)': {
    marginLeft: `${SIDEBAR_COLLAPSED_WIDTH}px`,
  },
  // target phone
  '@media screen and (max-width: 480px)': {
    marginLeft: '0 auto',
  },
});

const contentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'auto',
  marginBottom: '104px',
  padding: {
    xs: backrs.spacing['spacing-none'],
    md: backrs.spacing['spacing-2xl'],
  },
  marginLeft: backrs.spacing['spacing-2xl'],
  marginRight: backrs.spacing['spacing-2xl'],
  boxSizing: 'border-box',

  // target chromebook 720p
  '@media screen and (max-width: 1366px) and (max-height: 768px)': {
    marginLeft: `${SIDEBAR_COLLAPSED_WIDTH}px`,
    marginRight: `0px`,
  },

  // target iPad Mini
  '@media screen and (max-width: 1024px) and (max-height: 768px)': {
    marginLeft: `${SIDEBAR_COLLAPSED_WIDTH}px`,
    marginRight: `0px`,
  },

  // target medium size tablets
  '@media screen and (max-width: 1205px) and (max-height: 768px)': {
    marginLeft: `${SIDEBAR_COLLAPSED_WIDTH}px`,
    marginRight: `0px`,
  },
});

const paginationContainerSx: SxProps<Theme> = {
  width: '100%',
  minWidth: '100%',
  display: 'flex',
  justifyContent: 'center',
};
